import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { AlertCircle, Clapperboard, MoreHorizontal, Pencil, Play, Plus, SearchX, Trash2 } from "lucide-react";
import {
  Banner,
  Button,
  Card,
  Chip,
  DataTable,
  EmptyState,
  FilterMenu,
  Page,
  PageHeader,
  Toolbar,
  type ChipTone,
  type DataTableColumn,
  type FilterOption,
} from "@/components/ds";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { SplashFormPanel, appOptionLabel, defaultSplashApp } from "@/components/app-splashes/SplashFormPanel";
import { SplashPreview, splashFileName, type SplashPreviewSource } from "@/components/app-splashes/SplashPreview";
import { WhatPhonePlaysPanel } from "@/components/app-splashes/WhatPhonePlaysPanel";
import { useAppSplashApps, useAppSplashes, useDeleteAppSplash, useSetAppSplashActive } from "@/hooks/useAppSplashes";
import { formatDubaiDateTime } from "@/lib/dubaiDate";
import { SPLASH_STATUS_LABELS, splashStatus, type SplashStatus } from "@/lib/splash/splashStatus";
import type { AppSplashResponse } from "@/types/generated/cms.generated";

const ALL_APPS = "all";
/** Columns dropped below the sm breakpoint (390 px phones keep Name, Status and the actions). */
const NARROW_HIDDEN = "hidden sm:table-cell";

const STATUS_TONES: Record<SplashStatus, ChipTone> = {
  live: "ok",
  scheduled: "warn",
  ended: "neutral",
  off: "neutral",
};

/** The bundled splashes (no server rows behind them). */
const BUILT_INS = [
  { id: "athlyn", title: "Athlyn — D surge (3 s)" },
  { id: "amplify", title: "Amplify+ — logo" },
] as const;
const BUILT_IN_NOTE = "Plays whenever no occasion splash is live";

/** "Now", refreshed every minute so the chips move on while the page is open. */
function useNow(intervalMs = 60_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return now;
}

/**
 * Below sm, Edit and Delete fold into one "More" menu so Preview, the switch and the menu fit a
 * 390 px phone. Non-modal, so the dialog it opens is not fighting the menu's focus trap; it hands
 * its own trigger over as the opener so focus comes back to it.
 */
function RowMoreMenu({
  name,
  onEdit,
  onDelete,
}: {
  name: string;
  onEdit: (opener: HTMLElement | null) => void;
  onDelete: (opener: HTMLElement | null) => void;
}) {
  const trigger = useRef<HTMLButtonElement>(null);
  return (
    <span className="sm:hidden">
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button ref={trigger} size="sm" variant="ghost" className="px-2" aria-label={`More actions for ${name}`}>
            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="border-ds-line bg-ds-panel font-ds text-ds-ink">
          <DropdownMenuItem className="text-[13px]" onSelect={() => onEdit(trigger.current)}>
            <Pencil className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem className="text-[13px] text-ds-err" onSelect={() => onDelete(trigger.current)}>
            <Trash2 className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}

/** Row actions must not also open the row's Edit panel. */
const stop = (event: MouseEvent | KeyboardEvent) => event.stopPropagation();

const ConfirmContent = ({
  title,
  text,
  confirm,
  pending,
  onConfirm,
  testId,
  onCloseAutoFocus,
}: {
  title: string;
  text: string;
  confirm: string;
  pending: boolean;
  onConfirm: () => void;
  testId: string;
  onCloseAutoFocus: (event: Event) => void;
}) => (
  <AlertDialogContent
    className="rounded-ds-lg border-ds-line bg-ds-panel font-ds text-ds-ink shadow-ds"
    data-testid={testId}
    onCloseAutoFocus={onCloseAutoFocus}
  >
    <AlertDialogHeader>
      <AlertDialogTitle className="text-[16px] font-semibold text-ds-ink">{title}</AlertDialogTitle>
      <AlertDialogDescription className="text-[13.5px] text-ds-ink2">{text}</AlertDialogDescription>
    </AlertDialogHeader>
    <AlertDialogFooter>
      <AlertDialogPrimitive.Cancel asChild>
        <Button variant="secondary" disabled={pending}>
          Cancel
        </Button>
      </AlertDialogPrimitive.Cancel>
      <Button variant="danger" loading={pending} onClick={onConfirm}>
        {confirm}
      </Button>
    </AlertDialogFooter>
  </AlertDialogContent>
);

/** Splash screens — what phones show while the app opens; occasion splashes (Athlyn staff only). */
export default function SplashScreensPage() {
  const now = useNow();
  const list = useAppSplashes();
  const appsQuery = useAppSplashApps();
  const setActive = useSetAppSplashActive();
  const remove = useDeleteAppSplash();

  const [appFilter, setAppFilter] = useState(ALL_APPS);
  const [form, setForm] = useState<{ key: number; splash: AppSplashResponse | null; open: boolean } | null>(null);
  const [preview, setPreview] = useState<SplashPreviewSource | null>(null);
  const [toDelete, setToDelete] = useState<AppSplashResponse | null>(null);
  const [toSwitchOff, setToSwitchOff] = useState<AppSplashResponse | null>(null);
  const [switchingId, setSwitchingId] = useState<string | null>(null);

  const apps = useMemo(() => appsQuery.data ?? [], [appsQuery.data]);
  const rows = useMemo(() => list.data ?? [], [list.data]);
  // "<name> (<package>)", like the select, so live and Dev packages are told apart.
  const appLabel = (appScheme: string | null | undefined) => {
    const app = apps.find((a) => a.appScheme === appScheme);
    return app ? appOptionLabel(app) : appScheme ?? "";
  };

  const filterOptions: FilterOption[] = useMemo(
    () => [{ value: ALL_APPS, label: "All apps" }, ...apps.map((a) => ({ value: a.appScheme ?? "", label: appOptionLabel(a) }))],
    [apps]
  );
  const filtered = useMemo(
    () => (appFilter === ALL_APPS ? rows : rows.filter((r) => r.appScheme === appFilter)),
    [rows, appFilter]
  );

  // The explainer and a new splash follow the filter's app, else the Athlyn app (not Amplify+).
  const panelApp = defaultSplashApp(apps, appFilter);

  // The panel and the confirms are opened from state, not a Radix Trigger, so focus is handed back
  // by hand: to the control that opened them, else (its row was deleted) to Add splash.
  const opener = useRef<HTMLElement | null>(null);
  const addButton = useRef<HTMLButtonElement>(null);
  const focused = () => (document.activeElement instanceof HTMLElement ? document.activeElement : null);
  const rememberOpener = (from?: HTMLElement | null) => {
    opener.current = from ?? focused();
  };
  const restoreFocus = (event: Event) => {
    event.preventDefault();
    const target = opener.current?.isConnected ? opener.current : addButton.current;
    target?.focus();
  };

  const openForm = (splash: AppSplashResponse | null, from?: HTMLElement | null) => {
    rememberOpener(from);
    setForm((f) => ({ key: (f?.key ?? 0) + 1, splash, open: true }));
  };
  const askDelete = (splash: AppSplashResponse, from?: HTMLElement | null) => {
    rememberOpener(from);
    setToDelete(splash);
  };

  const switchTo = async (splash: AppSplashResponse, isActive: boolean) => {
    if (!splash.id) return;
    setSwitchingId(splash.id);
    try {
      await setActive.mutateAsync({ id: splash.id, isActive });
    } catch {
      // The hook toasts the server's sentence; the switch keeps the server's state (off).
    } finally {
      setSwitchingId(null);
    }
  };

  const onSwitch = (splash: AppSplashResponse, next: boolean) => {
    // Switching off a live splash asks first; every other switch is immediate.
    if (!next && splashStatus(splash, now) === "live") {
      rememberOpener();
      setToSwitchOff(splash);
    }
    else void switchTo(splash, next);
  };

  // The preview has its own opener: it can open from inside the side panel (Preview file),
  // whose own opener must survive it.
  const previewOpener = useRef<HTMLElement | null>(null);
  const openPreview = (source: SplashPreviewSource) => {
    previewOpener.current = focused();
    setPreview(source);
  };
  const restorePreviewFocus = (event: Event) => {
    event.preventDefault();
    const target = previewOpener.current?.isConnected ? previewOpener.current : addButton.current;
    target?.focus();
  };

  const previewSplash = (splash: AppSplashResponse) =>
    openPreview({
      kind: "url",
      url: splash.blobUrl ?? "",
      mediaType: splash.mediaType ?? "Video",
      title: `${splash.name ?? ""} · ${appLabel(splash.appScheme)}`,
      fileName: splashFileName(splash.blobUrl),
    });

  const columns: DataTableColumn<AppSplashResponse>[] = [
    {
      id: "name",
      header: "Name",
      cell: (r) => (
        <>
          <b className="font-semibold text-ds-ink">{r.name}</b>
          {/* Below sm the App / Type / Starts / Ends columns are hidden: their gist moves here. */}
          <small className="block text-[12px] text-ds-ink3 sm:hidden" data-testid="splash-row-summary">
            {appLabel(r.appScheme)} · {formatDubaiDateTime(r.startsAt)} – {formatDubaiDateTime(r.endsAt)}
          </small>
        </>
      ),
    },
    { id: "app", header: "App", className: NARROW_HIDDEN, cell: (r) => appLabel(r.appScheme) },
    { id: "type", header: "Type", className: NARROW_HIDDEN, cell: (r) => (r.mediaType === "Lottie" ? "Lottie" : "Video") },
    {
      id: "starts",
      header: "Starts",
      className: NARROW_HIDDEN,
      cell: (r) => <span className="whitespace-nowrap font-ds-mono text-[12.5px]">{formatDubaiDateTime(r.startsAt)}</span>,
    },
    {
      id: "ends",
      header: "Ends",
      className: NARROW_HIDDEN,
      cell: (r) => <span className="whitespace-nowrap font-ds-mono text-[12.5px]">{formatDubaiDateTime(r.endsAt)}</span>,
    },
    {
      id: "status",
      header: "Status",
      cell: (r) => {
        const status = splashStatus(r, now);
        return (
          <Chip tone={STATUS_TONES[status]} dot={status === "live"}>
            {SPLASH_STATUS_LABELS[status]}
          </Chip>
        );
      },
    },
    {
      id: "actions",
      header: <span className="sr-only">Actions</span>,
      align: "right",
      cell: (r) => (
        // The actions are their own controls: a click or key here never also opens the row's Edit.
        <div className="flex items-center justify-end gap-0.5 sm:gap-1" onClick={stop} onKeyDown={stop}>
          <Button size="sm" variant="ghost" className="px-2" aria-label={`Preview ${r.name ?? ""}`} onClick={() => previewSplash(r)}>
            <Play className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Switch
            aria-label={`Switch ${r.name ?? ""} on or off`}
            checked={r.isActive === true}
            disabled={switchingId === r.id}
            onCheckedChange={(next) => onSwitch(r, next)}
            className="mx-1 data-[state=checked]:bg-ds-accent data-[state=unchecked]:bg-ds-line2 focus-visible:ring-ds-accent"
          />
          <span className="hidden items-center gap-1 sm:flex">
            <Button size="sm" variant="ghost" aria-label={`Edit ${r.name ?? ""}`} onClick={() => openForm(r)}>
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button size="sm" variant="ghost" aria-label={`Delete ${r.name ?? ""}`} onClick={() => askDelete(r)}>
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </Button>
          </span>
          <RowMoreMenu name={r.name ?? ""} onEdit={(from) => openForm(r, from)} onDelete={(from) => askDelete(r, from)} />
        </div>
      ),
    },
  ];

  const ready = list.isSuccess;
  const countLabel = `${filtered.length} ${filtered.length === 1 ? "splash" : "splashes"}`;

  return (
    <Page data-testid="splash-screens-page">
      <PageHeader
        title="Splash screens"
        subtitle="What phones show while the app opens. Occasion splashes replace the built-in one for their dates."
        actions={
          <Button
            ref={addButton}
            variant="primary"
            icon={<Plus className="h-4 w-4" aria-hidden="true" />}
            onClick={() => openForm(null)}
            disabled={!appsQuery.isSuccess || apps.length === 0}
            data-testid="add-splash"
          >
            Add splash
          </Button>
        }
      />

      <section aria-labelledby="splash-built-in" className="mb-4">
        <h2 id="splash-built-in" className="mb-2 text-[14px] font-semibold text-ds-ink">
          Built-in
        </h2>
        <div className="grid gap-3 sm:grid-cols-2" data-testid="splash-built-ins">
          {BUILT_INS.map((b) => (
            <Card key={b.id} className="flex items-center gap-3 px-3.5 py-3" data-testid={`splash-built-in-${b.id}`}>
              <span aria-hidden="true" className="grid aspect-[9/16] w-9 flex-none place-items-center rounded-md bg-ds-nav">
                <Clapperboard className="h-3.5 w-3.5 text-ds-navink" />
              </span>
              <div className="min-w-0">
                <b className="block text-[13.5px] text-ds-ink">{b.title}</b>
                <small className="block text-[12.5px] text-ds-ink3">{BUILT_IN_NOTE}</small>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section aria-labelledby="splash-occasions" className="mb-4">
        <h2 id="splash-occasions" className="mb-2 text-[14px] font-semibold text-ds-ink">
          Occasion splashes
        </h2>
        {list.isError ? (
          <Banner
            tone="err"
            icon={AlertCircle}
            action={
              <Button size="sm" variant="secondary" onClick={() => void list.refetch()}>
                Try again
              </Button>
            }
          >
            <b>Couldn't load the splash screens.</b> Check your connection and try again. Phones keep playing what they
            already have.
          </Banner>
        ) : (
          <Card>
            <Toolbar
              filters={<FilterMenu label="App" value={appFilter} options={filterOptions} onChange={setAppFilter} />}
              count={ready ? countLabel : undefined}
            />
            <DataTable
              label="Occasion splashes"
              columns={columns}
              rows={filtered}
              getRowId={(r) => r.id ?? ""}
              onRowClick={(r) => openForm(r)}
              isLoading={list.isLoading}
              empty={
                rows.length === 0 ? (
                  <EmptyState icon={Clapperboard} title="No occasion splashes yet." text="Phones show the built-in splash." />
                ) : (
                  <EmptyState
                    icon={SearchX}
                    title="No occasion splashes for this app."
                    action={{ label: "Show all apps", onClick: () => setAppFilter(ALL_APPS) }}
                  />
                )
              }
            />
          </Card>
        )}
        <p className="mt-2 text-xs text-ds-ink3">Times are Dubai time. Phones download a splash up to 14 days before it starts.</p>
      </section>

      {panelApp?.appScheme && (
        <div className="max-w-[560px]">
          <WhatPhonePlaysPanel splashes={rows} appScheme={panelApp.appScheme} appLabel={appOptionLabel(panelApp)} now={now} />
        </div>
      )}

      {form && (
        <SplashFormPanel
          key={form.key}
          open={form.open}
          splash={form.splash}
          apps={apps}
          onClose={() => setForm((f) => (f ? { ...f, open: false } : f))}
          onCloseAutoFocus={restoreFocus}
          onPreview={openPreview}
          defaultAppScheme={panelApp?.appScheme ?? undefined}
        />
      )}

      <SplashPreview source={preview} onClose={() => setPreview(null)} onCloseAutoFocus={restorePreviewFocus} />

      <AlertDialog open={toDelete !== null} onOpenChange={(open) => !open && !remove.isPending && setToDelete(null)}>
        {toDelete && (
          <ConfirmContent
            testId="splash-delete-confirm"
            onCloseAutoFocus={restoreFocus}
            title={`Delete "${toDelete.name ?? ""}"?`}
            text="Phones stop showing it from their next-but-one open. This can't be undone."
            confirm="Delete"
            pending={remove.isPending}
            onConfirm={() => {
              if (!toDelete.id) return;
              remove.mutate(toDelete.id, { onSettled: () => setToDelete(null) });
            }}
          />
        )}
      </AlertDialog>

      <AlertDialog open={toSwitchOff !== null} onOpenChange={(open) => !open && setToSwitchOff(null)}>
        {toSwitchOff && (
          <ConfirmContent
            testId="splash-switch-off-confirm"
            onCloseAutoFocus={restoreFocus}
            title={`Switch off "${toSwitchOff.name ?? ""}"?`}
            text="Phones go back to the built-in splash from their next-but-one open."
            confirm="Switch off"
            pending={switchingId === toSwitchOff.id}
            onConfirm={() => {
              const splash = toSwitchOff;
              void switchTo(splash, false).finally(() => setToSwitchOff(null));
            }}
          />
        )}
      </AlertDialog>
    </Page>
  );
}
